// legalAcceptance.js — PLATFORM-002 / ADR-058 (clickwrap enforcement)
//
// Authenticated data-access layer for recording Terms/Privacy acceptance. Same
// JWT-Bearer pattern as lib/dashboardMetrics / lib/opportunitySignals: pull the
// live access token and POST to /api/accept-terms, which writes the profiles
// mirror + the append-only legal_acceptances audit (server-authoritative
// version). Returns the shared { data, error } shape and never throws.

import { supabase } from './supabaseClient';
import { API_BASE_URL } from '../services/api';

const NOT_SIGNED_IN = {
  data: null,
  error: { message: 'Not signed in. Please sign in and try again.' },
};

const NOT_CONFIGURED = {
  data: null,
  error: {
    message:
      'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
  },
};

/**
 * Record the signed-in user's affirmative acceptance of the current Terms of
 * Service and Privacy Policy. Returns { data: { accepted }, error }.
 */
export async function acceptLegal() {
  if (!supabase) return NOT_CONFIGURED;

  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return NOT_SIGNED_IN;

  try {
    const response = await fetch(`${API_BASE_URL}/accept-terms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ accept: true }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      return {
        data: null,
        error: {
          message: payload?.error || `Request failed (HTTP ${response.status}).`,
          status: response.status,
        },
      };
    }
    return { data: payload, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || 'Network error.' } };
  }
}
